/**
 * BeraOpportunity V1 - Algoritmo de Distribuição Justa de Visibilidade
 * 
 * Objetivo: Garantir que todos os proprietários tenham visibilidade justa,
 * evitando que apenas alguns espaços dominem as primeiras posições.
 */

export interface PropertyScore {
  propertyId: string;
  ownerId: string;
  qualityScore: number;
  popularityScore: number;
  freshnessScore: number;
  exposurePenalty: number;
  finalScore: number;
}

export interface BeraOpportunityConfig {
  qualityWeight: number;
  popularityWeight: number;
  freshnessWeight: number;
  exposurePenaltyWeight: number;
  maxPropertiesPerOwner: number;
  rotationEnabled: boolean;
  boostNewProperties: boolean;
}

export const DEFAULT_CONFIG: BeraOpportunityConfig = {
  qualityWeight: 0.3,
  popularityWeight: 0.25,
  freshnessWeight: 0.2,
  exposurePenaltyWeight: 0.25,
  maxPropertiesPerOwner: 3,
  rotationEnabled: true,
  boostNewProperties: true,
};

// Quality factors
interface QualityFactors {
  hasImages: boolean;
  imageCount: number;
  hasDescription: boolean;
  descriptionLength: number;
  hasAmenities: boolean;
  amenityCount: number;
  isVerified: boolean;
  hasCompleteProfile: boolean;
}

// Calculate quality score (0-100)
export function calculateQualityScore(property: any): number {
  const factors: QualityFactors = {
    hasImages: property.images && property.images.length > 0,
    imageCount: property.images?.length || 0,
    hasDescription: !!property.description && property.description.length > 50,
    descriptionLength: property.description?.length || 0,
    hasAmenities: property.amenities && property.amenities.length > 0,
    amenityCount: property.amenities?.length || 0,
    isVerified: property.is_verified === true,
    hasCompleteProfile: !!property.property_type && !!property.city && !!property.price_per_night,
  };

  let score = 0;

  // Images (max 30 points)
  if (factors.hasImages) {
    score += Math.min(30, factors.imageCount * 5);
  }

  // Description (max 25 points)
  if (factors.hasDescription) {
    score += Math.min(25, factors.descriptionLength / 10);
  }

  // Amenities (max 20 points)
  if (factors.hasAmenities) {
    score += Math.min(20, factors.amenityCount * 2);
  }

  // Verification (max 15 points)
  if (factors.isVerified) {
    score += 15;
  }

  // Complete profile (max 10 points)
  if (factors.hasCompleteProfile) {
    score += 10;
  }

  return Math.min(100, score);
}

// Calculate popularity score based on rating and reviews (0-100)
export function calculatePopularityScore(property: any): number {
  const rating = property.average_rating || 0;
  const reviewCount = property.total_reviews || 0;

  // Rating component (max 60 points)
  const ratingScore = Math.min(60, rating * 12);

  // Review count component (max 40 points) - logarithmic scale
  const reviewScore = Math.min(40, Math.log10(reviewCount + 1) * 15);

  return Math.min(100, ratingScore + reviewScore);
}

// Calculate freshness score - newer properties get boost (0-100)
export function calculateFreshnessScore(property: any): number {
  const createdAt = new Date(property.created_at);
  const now = new Date();
  const daysSinceCreation = Math.floor((now.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24));

  // New properties (less than 30 days) get maximum boost
  if (daysSinceCreation <= 30) {
    return 100;
  }

  // Gradual decrease over time
  if (daysSinceCreation <= 90) {
    return 80;
  }

  if (daysSinceCreation <= 180) {
    return 60;
  }

  if (daysSinceCreation <= 365) {
    return 40;
  }

  return 20; // Base score for older properties
}

// Calculate exposure penalty to prevent same owner dominating
export function calculateExposurePenalty(
  property: any,
  ownerPropertyCount: number,
  ownerPositionSum: number
): number {
  // More properties = higher penalty
  const countPenalty = Math.min(40, (ownerPropertyCount - 1) * 15);

  // Higher positions (better ranking) = higher penalty
  const positionPenalty = Math.min(30, ownerPositionSum * 5);

  return Math.min(70, countPenalty + positionPenalty);
}

// Main algorithm function
export function applyBeraOpportunityAlgorithm(
  properties: any[],
  config: BeraOpportunityConfig = DEFAULT_CONFIG,
  previousRotation: string[] = []
): PropertyScore[] {
  if (!properties || properties.length === 0) {
    return [];
  }

  // Group by owner
  const ownerGroups = new Map<string, any[]>();
  properties.forEach((prop) => {
    const ownerId = prop.owner_id || "unknown";
    if (!ownerGroups.has(ownerId)) {
      ownerGroups.set(ownerId, []);
    }
    ownerGroups.get(ownerId)!.push(prop);
  });

  // Calculate scores for each property
  const scoredProperties: PropertyScore[] = properties.map((prop, index) => {
    const ownerId = prop.owner_id || "unknown";
    const ownerProps = ownerGroups.get(ownerId) || [];
    const ownerPositionSum = ownerProps.reduce((sum, p) => {
      const pos = properties.findIndex((pp) => pp.id === p.id);
      return sum + (pos >= 0 ? pos : 0);
    }, 0);

    const qualityScore = calculateQualityScore(prop);
    const popularityScore = calculatePopularityScore(prop);
    const freshnessScore = calculateFreshnessScore(prop);
    const exposurePenalty = calculateExposurePenalty(prop, ownerProps.length, ownerPositionSum);

    // Calculate final weighted score
    const finalScore =
      qualityScore * config.qualityWeight +
      popularityScore * config.popularityWeight +
      freshnessScore * config.freshnessWeight -
      exposurePenalty * config.exposurePenaltyWeight;

    return {
      propertyId: prop.id,
      ownerId,
      qualityScore,
      popularityScore,
      freshnessScore,
      exposurePenalty,
      finalScore: Math.max(0, finalScore), // Ensure non-negative
    };
  });

  // Sort by final score descending
  scoredProperties.sort((a, b) => b.finalScore - a.finalScore);

  // Apply rotation if enabled
  if (config.rotationEnabled && previousRotation.length > 0) {
    return applyRotation(scoredProperties, previousRotation, config.maxPropertiesPerOwner);
  }

  return scoredProperties;
}

// Apply rotation to ensure fair exposure over time
function applyRotation(
  scoredProperties: PropertyScore[],
  previousRotation: string[],
  maxPerOwner: number
): PropertyScore[] {
  const ownerCount = new Map<string, number>();
  const rotated: PropertyScore[] = [];
  const delayed: PropertyScore[] = [];

  // First pass: respect max per owner and rotation
  scoredProperties.forEach((prop) => {
    const currentCount = ownerCount.get(prop.ownerId) || 0;

    if (currentCount >= maxPerOwner) {
      // Too many from this owner, delay to end
      delayed.push(prop);
    } else if (previousRotation.includes(prop.propertyId) && currentCount > 0) {
      // Was featured before, delay slightly for fairness
      delayed.push(prop);
    } else {
      rotated.push(prop);
      ownerCount.set(prop.ownerId, currentCount + 1);
    }
  });

  // Add delayed properties at the end
  return [...rotated, ...delayed];
}

// Get sorted properties with algorithm applied
export function getBeraOpportunitySortedProperties(
  properties: any[],
  config?: Partial<BeraOpportunityConfig>,
  previousRotation?: string[]
): any[] {
  const fullConfig = { ...DEFAULT_CONFIG, ...config };
  const scored = applyBeraOpportunityAlgorithm(properties, fullConfig, previousRotation);

  // Map back to original properties in new order
  const propertyMap = new Map(properties.map((p) => [p.id, p]));

  return scored
    .map((score) => propertyMap.get(score.propertyId))
    .filter((p): p is any => p !== undefined);
}

// Algorithm analytics for admin monitoring
export interface AlgorithmAnalytics {
  totalProperties: number;
  uniqueOwners: number;
  averageQualityScore: number;
  averagePopularityScore: number;
  averageFreshnessScore: number;
  averageExposurePenalty: number;
  topOwners: { ownerId: string; propertyCount: number; avgScore: number }[];
  scoreDistribution: { range: string; count: number }[];
}

export function generateAlgorithmAnalytics(scoredProperties: PropertyScore[]): AlgorithmAnalytics {
  if (scoredProperties.length === 0) {
    return {
      totalProperties: 0,
      uniqueOwners: 0,
      averageQualityScore: 0,
      averagePopularityScore: 0,
      averageFreshnessScore: 0,
      averageExposurePenalty: 0,
      topOwners: [],
      scoreDistribution: [],
    };
  }

  const ownerStats = new Map<string, { count: number; scores: number[] }>();

  scoredProperties.forEach((prop) => {
    const stats = ownerStats.get(prop.ownerId) || { count: 0, scores: [] };
    stats.count++;
    stats.scores.push(prop.finalScore);
    ownerStats.set(prop.ownerId, stats);
  });

  const topOwners = Array.from(ownerStats.entries())
    .map(([ownerId, stats]) => ({
      ownerId,
      propertyCount: stats.count,
      avgScore: stats.scores.reduce((a, b) => a + b, 0) / stats.count,
    }))
    .sort((a, b) => b.avgScore - a.avgScore)
    .slice(0, 10);

  const scoreDistribution = [
    { range: "90-100", count: scoredProperties.filter((p) => p.finalScore >= 90).length },
    { range: "80-89", count: scoredProperties.filter((p) => p.finalScore >= 80 && p.finalScore < 90).length },
    { range: "70-79", count: scoredProperties.filter((p) => p.finalScore >= 70 && p.finalScore < 80).length },
    { range: "60-69", count: scoredProperties.filter((p) => p.finalScore >= 60 && p.finalScore < 70).length },
    { range: "50-59", count: scoredProperties.filter((p) => p.finalScore >= 50 && p.finalScore < 60).length },
    { range: "0-49", count: scoredProperties.filter((p) => p.finalScore < 50).length },
  ];

  return {
    totalProperties: scoredProperties.length,
    uniqueOwners: ownerStats.size,
    averageQualityScore: scoredProperties.reduce((a, b) => a + b.qualityScore, 0) / scoredProperties.length,
    averagePopularityScore: scoredProperties.reduce((a, b) => a + b.popularityScore, 0) / scoredProperties.length,
    averageFreshnessScore: scoredProperties.reduce((a, b) => a + b.freshnessScore, 0) / scoredProperties.length,
    averageExposurePenalty: scoredProperties.reduce((a, b) => a + b.exposurePenalty, 0) / scoredProperties.length,
    topOwners,
    scoreDistribution,
  };
}

export default {
  DEFAULT_CONFIG,
  calculateQualityScore,
  calculatePopularityScore,
  calculateFreshnessScore,
  applyBeraOpportunityAlgorithm,
  getBeraOpportunitySortedProperties,
  generateAlgorithmAnalytics,
};
